import { useState } from 'react';
import {
  createFlowExecutions,
  archiveFlows,
  pauseScheduledFlow,
  pauseActiveFlow,
  resumePausedFlows,
} from '../../../api/flows';
import { testCall } from '../../../api/flowWorker';
import { useOrganization } from '../../../hooks/useOrganization';

export function TestFlows() {
  const [result, setResult] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [flowId, setFlowId] = useState<string>('');
  const [contactId, setContactId] = useState<string>('');
  const { organization } = useOrganization();

  const handleAction = async (action: () => Promise<any>, actionName: string) => {
    if (!flowId.trim()) {
      setResult('Please enter a flow ID');
      return;
    }

    setLoading(true);
    try {
      const response = await action();
      setResult(`${actionName} successful. Response: ${JSON.stringify(response, null, 2)}`);
    } catch (error: any) {
      setResult(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestCall = async () => {
    if (!flowId.trim() || !contactId.trim() || !organization?.id) {
      setResult('Please enter flow ID, contact ID, and ensure organization is selected');
      return;
    }

    setLoading(true);
    try {
      const response = await testCall({
        flowId,
        contactId,
        organizationId: organization.id,
      });
      setResult(`Test call successful. Response: ${JSON.stringify(response, null, 2)}`);
    } catch (error: any) {
      setResult(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 border rounded-lg mt-4">
      <h2 className="text-lg font-semibold mb-4">Flow Operations Test</h2>

      <div className="space-y-4">
        <div className="mb-4">
          <label htmlFor="flowId" className="block text-sm font-medium mb-2">
            Flow ID
          </label>
          <input
            id="flowId"
            type="text"
            value={flowId}
            onChange={(e) => setFlowId(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            placeholder="Enter flow ID"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="contactId" className="block text-sm font-medium mb-2">
            Contact ID (for test call)
          </label>
          <input
            id="contactId"
            type="text"
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            placeholder="Enter contact ID for test call"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() =>
              handleAction(() => createFlowExecutions(flowId), 'Create Flow Executions')
            }
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
          >
            Create Flow Executions
          </button>

          <button
            onClick={() => handleAction(() => archiveFlows(flowId), 'Archive Flows')}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
          >
            Archive Flows
          </button>

          <button
            onClick={() => handleAction(() => pauseScheduledFlow(flowId), 'Pause Scheduled Flow')}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
          >
            Pause Scheduled Flow
          </button>

          <button
            onClick={() => handleAction(() => pauseActiveFlow(flowId), 'Pause Active Flow')}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
          >
            Pause Active Flow
          </button>

          <button
            onClick={() => handleAction(() => resumePausedFlows(flowId), 'Resume Paused Flows')}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
          >
            Resume Paused Flows
          </button>

          <button
            onClick={handleTestCall}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
          >
            Test Call
          </button>
        </div>

        {loading && <div>Loading...</div>}

        {result && (
          <div className="mt-4 p-4 bg-gray-100 rounded">
            <pre className="whitespace-pre-wrap">{result}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
